<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  Bell,
  CheckCheck,
  Calendar,
  CreditCard,
  Video,
  Activity,
  ClipboardList,
  Sparkles,
  X,
  ChevronRight,
  Loader2,
  RefreshCw
} from 'lucide-vue-next';
import { notificationsApi } from '@/services/api';
import { useTenant } from '@/composables/useTenant';
import type { NotificationItem } from '@/types/models';

export type { NotificationItem };

const { t } = useI18n();
const router = useRouter();
const { activeTenantId } = useTenant();

const isOpen = ref(false);
const isLoading = ref(false);
const notifications = ref<NotificationItem[]>([]);

const unreadCount = computed(() => notifications.value.filter(n => !n.read).length);

// Fetch notifications from API
async function fetchNotifications() {
  isLoading.value = true;
  try {
    const data = await notificationsApi.getNotifications({
      tenantId: activeTenantId.value,
    });
    notifications.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('[NotificationPopover] Failed to fetch from API:', err);
  } finally {
    isLoading.value = false;
  }
}

// Re-fetch when active tenant changes
watch(activeTenantId, () => {
  fetchNotifications();
});

onMounted(() => {
  fetchNotifications();
});

const getIcon = (type: NotificationItem['type']) => {
  switch (type) {
    case 'lineup': return ClipboardList;
    case 'video': return Video;
    case 'pitch_smart': return Activity;
    case 'billing': return CreditCard;
    case 'event': return Calendar;
    default: return Sparkles;
  }
};

const getIconColor = (type: NotificationItem['type']) => {
  switch (type) {
    case 'lineup': return 'bg-sky-500/15 text-sky-400 border-sky-500/30';
    case 'video': return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
    case 'pitch_smart': return 'bg-rose-500/15 text-rose-400 border-rose-500/30';
    case 'billing': return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
    default: return 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30';
  }
};

const markAllAsRead = async () => {
  notifications.value.forEach(n => (n.read = true));
  try {
    await notificationsApi.markAllAsRead(activeTenantId.value);
  } catch (e) {
    console.warn('[NotificationPopover] markAllAsRead sync error:', e);
  }
};

const handleNotificationClick = async (item: NotificationItem) => {
  item.read = true;
  isOpen.value = false;
  try {
    await notificationsApi.markAsRead(item.id);
  } catch (e) {
    // Non-blocking
  }
  if (item.route) {
    router.push(item.route);
  }
};

const dismissNotification = async (id: string, e: Event) => {
  e.stopPropagation();
  notifications.value = notifications.value.filter(n => n.id !== id);
  try {
    await notificationsApi.dismissNotification(id);
  } catch (e) {
    console.warn('[NotificationPopover] dismissNotification sync error:', e);
  }
};
</script>

<template>
  <div class="relative">
    <!-- Bell Trigger Button -->
    <button
      type="button"
      @click="isOpen = !isOpen"
      class="p-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-800 relative transition shadow-sm focus:outline-none"
      :title="t('nav.notifications')"
      aria-label="Notifications"
    >
      <Bell class="w-4 h-4" />
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-sky-500 text-slate-950 font-black text-[10px] flex items-center justify-center ring-2 ring-slate-950 animate-pulse"
      >
        {{ unreadCount }}
      </span>
    </button>

    <!-- Dropdown Menu Popover -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-80 sm:w-96 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
      style="background-color: #0f172a;"
    >
      <!-- Popover Header -->
      <div class="px-4 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-900" style="background-color: #0f172a;">
        <div class="flex items-center gap-2">
          <h3 class="text-xs font-extrabold uppercase tracking-wider text-slate-100">{{ t('nav.notifications') }}</h3>
          <span v-if="unreadCount > 0" class="px-1.5 py-0.5 rounded-md bg-sky-500/20 text-sky-300 font-mono text-[10px] font-bold">
            {{ unreadCount }} new
          </span>
          <span v-else class="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> API live
          </span>
        </div>

        <div class="flex items-center gap-2">
          <button
            type="button"
            @click="fetchNotifications"
            class="p-1 text-slate-400 hover:text-slate-200 transition rounded-md hover:bg-slate-800"
            title="Refresh notifications from API"
          >
            <Loader2 v-if="isLoading" class="w-3.5 h-3.5 animate-spin text-sky-400" />
            <RefreshCw v-else class="w-3.5 h-3.5" />
          </button>

          <button
            v-if="unreadCount > 0"
            type="button"
            @click="markAllAsRead"
            class="text-[11px] font-semibold text-sky-400 hover:text-sky-300 flex items-center gap-1 transition"
          >
            <CheckCheck class="w-3.5 h-3.5" />
            <span>Mark all read</span>
          </button>
        </div>
      </div>

      <!-- Notification items stream -->
      <div class="max-h-80 overflow-y-auto divide-y divide-slate-800/60 bg-slate-900" style="background-color: #0f172a;">
        <div
          v-for="item in notifications"
          :key="item.id"
          @click="handleNotificationClick(item)"
          :class="[
            'p-3.5 transition flex items-start gap-3 cursor-pointer group relative',
            !item.read ? 'bg-slate-800/40 hover:bg-slate-800/80' : 'hover:bg-slate-800/50 opacity-80 hover:opacity-100'
          ]"
        >
          <!-- Category icon -->
          <div :class="['w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border mt-0.5', getIconColor(item.type)]">
            <component :is="getIcon(item.type)" class="w-4 h-4" />
          </div>

          <!-- Text copy -->
          <div class="flex-1 min-w-0 pr-4">
            <div class="flex items-center justify-between gap-1">
              <h4 :class="['text-xs font-bold truncate', !item.read ? 'text-slate-100' : 'text-slate-300']">
                {{ item.title }}
              </h4>
              <span class="text-[10px] text-slate-400 font-mono shrink-0">{{ item.time }}</span>
            </div>
            <p class="text-[11px] text-slate-400 line-clamp-2 mt-0.5 leading-snug">
              {{ item.message }}
            </p>
          </div>

          <!-- Unread indicator dot & dismiss -->
          <button
            type="button"
            @click="dismissNotification(item.id, $event)"
            class="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-slate-300 transition rounded hover:bg-slate-700/50"
            title="Dismiss"
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="isLoading && notifications.length === 0" class="py-8 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
          <Loader2 class="w-5 h-5 animate-spin text-sky-400" />
          <span>Fetching notifications from API...</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="notifications.length === 0" class="py-8 text-center text-xs text-slate-500">
          No notifications right now
        </div>
      </div>

      <!-- Popover Footer Quick Links -->
      <div class="p-2 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between text-[11px]">
        <span class="text-slate-400 px-2">Next Base Team Cloud Hub</span>
        <button
          type="button"
          @click="isOpen = false; router.push('/dashboard/events')"
          class="text-sky-400 hover:text-sky-300 font-semibold flex items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-slate-800 transition"
        >
          View Schedule <ChevronRight class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- Backdrop dismiss overlay -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="isOpen = false" />
  </div>
</template>
